import { useState, useEffect } from 'react';
import { Progress } from '@components/base/feedback';
import { Button } from '@components/base/actions';
import { Text } from '@components/base/display';
import { VStack, HStack } from '@components/base/layout';
import { ShowcasePreviewSection } from '../../components/shared/ShowcasePreviewSection';
import { ShowcaseDemoSection } from '../../components/shared/ShowcaseDemoSection';
import type { ShowcaseDefinition, ShowcaseProps } from '../types';

type ProgressSize = 'thin' | 'default';
type ProgressVariant = 'primary' | 'success' | 'warning' | 'error' | 'info';

function ProgressShowcaseComponent({ playgroundProps, isDarkMode: _isDarkMode }: Readonly<ShowcaseProps>) {
  // State for animated progress demo
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);

  const startUpload = () => {
    setUploadProgress(0);
    setIsUploading(true);
  };

  useEffect(() => {
    if (isUploading && uploadProgress < 100) {
      const timer = setTimeout(() => {
        setUploadProgress((prev) => Math.min(prev + Math.random() * 15, 100));
      }, 300);
      return () => clearTimeout(timer);
    } else if (uploadProgress >= 100) {
      setIsUploading(false);
    }
  }, [isUploading, uploadProgress]);

  return (
    <VStack gap={6}>
      {/* Interactive Preview */}
      <ShowcasePreviewSection componentName="progress bar">
          <Progress
            value={playgroundProps.value as number}
            size={playgroundProps.size as ProgressSize}
            variant={playgroundProps.variant as ProgressVariant}
            showValue
          />
      </ShowcasePreviewSection>

      {/* Size Variants */}
      <ShowcaseDemoSection
        title="Size Variants"
        description="Two size options: thin (4px) and default (8px)"
       
      >
          <VStack gap={4}>
            <VStack gap={2}>
              <Text size="sm" weight="medium">Thin (4px)</Text>
              <Progress value={60} size="thin" />
            </VStack>
            <VStack gap={2}>
              <Text size="sm" weight="medium">Default (8px)</Text>
              <Progress value={60} size="default" />
            </VStack>
          </VStack>
      </ShowcaseDemoSection>

      {/* Color Variants */}
      <ShowcaseDemoSection
        title="Color Variants"
        description="Five semantic color variants for different contexts"
       
      >
          <VStack gap={4}>
            <VStack gap={2}>
              <Text size="sm" weight="medium">Primary</Text>
              <Progress value={75} variant="primary" />
            </VStack>
            <VStack gap={2}>
              <Text size="sm" weight="medium">Success</Text>
              <Progress value={100} variant="success" />
            </VStack>
            <VStack gap={2}>
              <Text size="sm" weight="medium">Warning</Text>
              <Progress value={85} variant="warning" />
            </VStack>
            <VStack gap={2}>
              <Text size="sm" weight="medium">Error</Text>
              <Progress value={30} variant="error" />
            </VStack>
            <VStack gap={2}>
              <Text size="sm" weight="medium">Info</Text>
              <Progress value={50} variant="info" />
            </VStack>
          </VStack>
      </ShowcaseDemoSection>

      {/* Indeterminate */}
      <ShowcaseDemoSection
        title="Indeterminate Progress"
        description="When duration is unknown, omit the value prop for an animated indicator"
       
      >
          <VStack gap={4}>
            <VStack gap={2}>
              <Text size="sm" weight="medium">Loading claims data...</Text>
              <Progress />
            </VStack>
            <VStack gap={2}>
              <Text size="sm" weight="medium">Processing policy updates...</Text>
              <Progress variant="info" />
            </VStack>
          </VStack>
      </ShowcaseDemoSection>

      {/* With Percentage Labels */}
      <ShowcaseDemoSection
        title="With Percentage Labels"
        description="Display the current progress value alongside the bar"
       
      >
          <VStack gap={4}>
            <Progress value={25} showValue />
            <Progress value={50} showValue variant="info" />
            <Progress value={75} showValue variant="warning" />
            <Progress value={100} showValue variant="success" />
          </VStack>
      </ShowcaseDemoSection>

      {/* Real-world Examples */}
      <ShowcaseDemoSection
        title="Real-world Examples"
        description="Common progress patterns in insurance applications"
       
      >
          <VStack gap={6}>
            {/* Document upload */}
            <VStack gap={2}>
              <HStack justify="between">
                <Text size="sm" weight="medium">Uploading claim documents...</Text>
                <Text size="sm" color="secondary">
                  {Math.round(uploadProgress)}%
                </Text>
              </HStack>
              <Progress
                value={uploadProgress}
                variant={uploadProgress >= 100 ? 'success' : 'primary'}
              />
              <HStack gap={2}>
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={startUpload}
                  disabled={isUploading}
                >
                  {isUploading ? 'Uploading...' : 'Simulate Upload'}
                </Button>
              </HStack>
            </VStack>

            {/* Form completion */}
            <VStack gap={2}>
              <HStack justify="between">
                <Text size="sm" weight="medium">Policy Application Progress</Text>
                <Text size="sm" color="secondary">Step 3 of 5</Text>
              </HStack>
              <Progress value={60} variant="primary" showValue />
              <Text size="xs" color="secondary">
                Complete all sections to submit your application
              </Text>
            </VStack>

            {/* Claims processing pipeline */}
            <VStack gap={3}>
              <Text size="sm" weight="semibold">Claim Processing Pipeline</Text>
              <VStack gap={2}>
                <HStack justify="between">
                  <Text size="sm">Document Review</Text>
                  <Text size="xs" color="secondary">Complete</Text>
                </HStack>
                <Progress value={100} variant="success" size="thin" />
              </VStack>
              <VStack gap={2}>
                <HStack justify="between">
                  <Text size="sm">Assessment</Text>
                  <Text size="xs" color="secondary">In Progress</Text>
                </HStack>
                <Progress value={65} variant="primary" size="thin" />
              </VStack>
              <VStack gap={2}>
                <HStack justify="between">
                  <Text size="sm">Approval</Text>
                  <Text size="xs" color="secondary">Pending</Text>
                </HStack>
                <Progress value={0} size="thin" />
              </VStack>
            </VStack>

            {/* Storage quota */}
            <VStack gap={2}>
              <HStack justify="between">
                <Text size="sm" weight="medium">Document Storage</Text>
                <Text size="sm" color="secondary">8.5 GB of 10 GB used</Text>
              </HStack>
              <Progress value={85} variant="warning" />
              <Text size="xs" color="secondary">
                Consider archiving old documents to free up space
              </Text>
            </VStack>
          </VStack>
      </ShowcaseDemoSection>
    </VStack>
  );
}

export const ProgressShowcase: ShowcaseDefinition = {
  id: 'progress',
  name: 'Progress',
  category: 'feedback',
  description: 'Visual indicator for task completion progress',
  propsDefinition: [
    {
      name: 'value',
      type: 'number',
      defaultValue: 50,
      description: 'Progress value from 0 to 100 (omit for indeterminate)',
    },
    {
      name: 'size',
      type: 'select',
      options: ['thin', 'default'],
      defaultValue: 'default',
      description: 'Height of the progress bar (thin=4px, default=8px)',
    },
    {
      name: 'variant',
      type: 'select',
      options: ['primary', 'success', 'warning', 'error', 'info'],
      defaultValue: 'primary',
      description: 'Color variant of the progress bar',
    },
  ],
  accessibility: {
    ariaAttributes: [
      'role="progressbar"',
      'aria-valuenow (current value)',
      'aria-valuemin="0"',
      'aria-valuemax="100"',
      'aria-valuetext (for indeterminate)',
    ],
    keyboardNav: [],
    notes: [
      'Progress changes are announced to screen readers',
      'Indeterminate mode uses aria-valuetext="Loading..."',
      'Respects prefers-reduced-motion preference',
    ],
  },
  component: ProgressShowcaseComponent,
};
